<template>
    <div>
        <skeleton-loader v-if="loading" type="lib-rows" :count="perPage" />

        <div v-else-if="prescriptions.length === 0" class="lib-empty">
            <small>{{ $t('common.no_prescriptions_founded') }}</small>
        </div>

        <div v-else class="lib-list">
            <prescription-card v-for="prescription in prescriptions" :key="prescription.objectID"
                :prescription="prescription" with-menu @click:edit="onEditPrescription(prescription)"
                @click:copy="confirmCopy(prescription)" no-pause-text @mousedown="startDrag"
                @click:share="showSharePrescriptionDialog(prescription)"
                @click:delete="showDeletePrescription(prescription.objectID)" :canShare="canShare" />
        </div>

        <kimsa-pagination class="mt-4" @page="onPageChange" :value="page + 1" :pageCount="pageCount" center
            :total-visible="isMobile ? 4 : (totalVisiblePagination || 7)" />

        <prescription-card v-if="dragging" :prescription="draggingItem" with-menu no-pause-text
            class="floating-drag-item" />

        <delete-dragging-button :dragging="dragMoved" @mouseup="showDeletePrescription(draggingItem.objectID)" />

        <kimsa-confirm ref="showDelete" :title="$t('common.delete_prescription')"
            :content="$t('common.confirm_deleted_prescription')" :persistent="false" :loading="loadingDelete"
            @confirm="deletePrescription" @cancel="prescriptionId = null" :close-on-confirm="false"
            :alert-mode="true" />

        <create-prescription-dialog ref="createPrescriptionDialog" @success="onPrescriptionSaved" :coachId="coachId" />

        <share-prescription-dialog ref="share" />

        <kimsa-confirm ref="confirmCopy" :title="$t('common.duplicate')" :content="$t('common.confirm_duplicate_prescription')"
            :persistent="false" :loading="loadingCopy" @confirm="onCopyPrescription" @cancel="copyItem = null"
            :close-on-confirm="false" />
    </div>
</template>

<script>

import PrescriptionCard from '@/components/common/routines/PrescriptionCard'
import { PrescriptionIndex } from "@/utils/Algolia";
import { mapActions } from "vuex";
import { db } from "@/utils/Firebase";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { debounce } from "@/utils/Common";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import KimsaPagination from "@/components/common/KimsaPagination";
import CreatePrescriptionDialog from './CreatePrescriptionDialog.vue';
import DeleteDraggingButton from '../../common/DeleteDraggingButton.vue';
import draggingMixin from '../../../mixins/draggingMixin';
import SharePrescriptionDialog from './SharePrescriptionDialog.vue';
import algoliaInlineItemMixin from '../../../mixins/algoliaInlineItemMixin';

export default {
    mixins: [draggingMixin, algoliaInlineItemMixin],
    components: {
        KimsaPagination,
        SkeletonLoader,
        KimsaConfirm,
        PrescriptionCard,
        CreatePrescriptionDialog,
        DeleteDraggingButton,
        SharePrescriptionDialog
    },
    props: {
        search: {
            type: String,
            default: '',
        },
        category: {
            type: String,
            default: '',
        },
        coachId: {
            type: String,
            default: null,
        },
        totalVisiblePagination: {
            type: [Number, String],
            default: null,
        }
    },
    data() {
        return {
            loading: false,
            prescriptions: [],
            prescriptionId: null,
            loadingDelete: false,
            coaches: [],
            page: 0,
            pageCount: 0,
            perPage: 6,
            copyItem: null,
            loadingCopy: false,
        }
    },
    computed: {
        companyId(vm = this) {
            return vm.account?.coachSubscription?.companyId || vm.currentUser.uid
        },
        canShare() {
            return this.isCompany || this.account?.accountType === 'coach_company'
        },
    },
    async mounted() {
        let vm = this
        vm.isCompany ? await vm.loadCoaches() : await vm.loadPrescriptions()
    },
    methods: {
        ...mapActions(['fetchCompanyCoaches']),
        async loadCoaches() {
            const vm = this
            const coaches = await vm.fetchCompanyCoaches(vm.companyId)
            vm.coaches = [vm.coachId, ...coaches.map(c => c.objectID)]
        },
        async showDeletePrescription(id) {
            const vm = this
            vm.resetDrag()
            vm.prescriptionId = id
            vm.$refs.showDelete.show()
        },
        async deletePrescription() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('prescriptions').doc(vm.prescriptionId).delete()
                vm.prescriptions = vm.markDeletedInline(vm.prescriptions, vm.prescriptionId)
                vm.toast(vm.$t('common.prescription_deleted'))
                vm.prescriptionId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
        debouncedLoadPrescriptions: debounce(
            function () {
                this.loadPrescriptions()
            }, 1000),
        async loadPrescriptions() {
            const vm = this
            await vm.withLoading(async () => {
                vm.prescriptions = []
                let coaches = vm.coaches.length === 0 ? vm.coachId : vm.coaches.join(' OR coachId:')
                let filter = `coachId: ${coaches}`
                if (vm.category) filter += ` AND category: ${vm.category}`
                let response = await PrescriptionIndex.search(vm.search, {
                    filters: filter,
                    hitsPerPage: vm.perPage,
                    page: vm.page,
                })
                vm.pageCount = response.nbPages || 0
                vm.prescriptions = vm.filterDeletedInline(response.hits)
            })
        },
        refresh() {
            const vm = this;
            vm.loadPrescriptions()
        },
        onPrescriptionSaved(prescription) {
            const vm = this
            if (!vm.addItemInline(vm.prescriptions, prescription, 'category', vm.category)) vm.loadPrescriptions()
        },
        onPageChange(p) {
            const vm = this
            const newPage = Number(p) - 1
            if (newPage === vm.page) return
            vm.page = newPage
            vm.loadPrescriptions()
        },
        onEditPrescription(prescription) {
            const vm = this
            vm.$refs.createPrescriptionDialog.setEditingMode(prescription)
        },
        confirmCopy(prescription) {
            const vm = this
            vm.resetDrag()
            vm.copyItem = prescription
            vm.$refs.confirmCopy.show()
        },
        async onCopyPrescription() {
            let vm = this
            const prescription = vm.copyItem
            await vm.withLoading(async () => {
                let current = await db.collection('prescriptions').doc(prescription.objectID).get()
                let payload = {
                    ...current.data(),
                    coachId: vm.coachId,
                    name: `${prescription.name} ${vm.$t('common.copy_template')}`,
                }
                delete payload.objectID
                payload = JSON.parse(JSON.stringify(payload))
                let ref = await db.collection('prescriptions').add({ ...payload, used: 0 })
                vm.toast(vm.$t('common.prescription_duplicated'))
                vm.$refs.confirmCopy?.hide()
                let newItem = { ...payload, used: 0, objectID: ref.id, id: ref.id }
                vm.prescriptions.unshift(newItem)
                vm.onEditPrescription(newItem)
            }, 'loadingCopy')
        },
        handleDoubleClickDragging(item) {
            const vm = this;
            vm.confirmCopy(item)
        },
        handleClickDragging(item) {
            const vm = this;
            vm.onEditPrescription(item)
        },
        showSharePrescriptionDialog(prescription) {
            const vm = this;
            vm.$refs.share.show(prescription)
        }
    },
    watch: {
        coaches() {
            this.loadPrescriptions()
        },
        search(val) {
            this.page = 0
            if (!val) return this.loadPrescriptions()
            return this.debouncedLoadPrescriptions()
        },
        category() {
            this.page = 0
            this.loadPrescriptions()
        },
        loading(val) {
            this.$emit('loading', val)
        },
    },
}
</script>

<style scoped>
.lib-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.lib-empty {
    text-align: center;
    padding: 32px 0;
    color: var(--v-txtMuted-base);
}

.floating-drag-item {
    position: fixed;
    z-index: 9999;
    opacity: var(--drag-opacity);
    width: 250px;
    left: 0;
    top: 0;
    transform: translate(var(--drag-x), var(--drag-y));
    pointer-events: none;
}
</style>
